// Copyright ©️ 2026 eVolve MEP, LLC

import { createContext, type ReactNode, useCallback, useMemo, useState } from 'react';

import type { BendCreatorValidationRequest, BendValidationResponse } from '@evolvemep/api-ts';
import { v4 as uuid } from 'uuid';

import { isNil } from 'helpers/isNotNil';
import { useGeneralContext } from 'helpers/useGeneralContext';
import type { ValidatedFormValues } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BendCreatorForm';
import { getBaseName, getFreeName } from 'modules/Shop/EvolveFabrication/BendCreator/State/bendNaming';

declare const cartBendIdSymbol: unique symbol;
export type CartBendId = string & { [cartBendIdSymbol]: never };

export type CartBend = {
  cartBendId: CartBendId;
  /** The bend's validated form values. */
  values: ValidatedFormValues;
  /** The validation the bend's values passed. */
  validation: { request: BendCreatorValidationRequest; response: BendValidationResponse };
  /** True while the name was set automatically, so a bend type change may rename it. */
  isAutoNamed: boolean;
  /** The bend this one was duplicated from, if any. Not shown yet; kept for linking variants later. */
  duplicatedFrom: CartBendId | null;
};

export type BendCartContextType = {
  bends: readonly CartBend[];
  /** Whether the bend list shows as a panel (true) or collapsed to a pill (false). Meaningless while it's empty. */
  cartOpen: boolean;
  setCartOpen: (cartOpen: boolean) => void;
  /** The bend shown on the model instead of the one being built, if any. */
  previewedBendId: CartBendId | null;
  /** Whether the previewed bend's row is marked as selected. Done on an expanded row clears the mark only. */
  previewMarked: boolean;
  /** The row open in place with its jump rows, if any. */
  expandedBendId: CartBendId | null;
  /** The row most recently added, which briefly shows a highlight. Cleared once another row is selected. */
  justAddedBendId: CartBendId | null;
  /** Adds a bend at the end of the list. The list opens itself for its first bend only. */
  addBend: (bend: Omit<CartBend, 'cartBendId'>) => CartBendId;
  /** Copies a bend to the end of the list with the next free name, then expands and previews the copy. */
  duplicateBend: (cartBendId: CartBendId) => CartBendId | null;
  updateBend: (cartBendId: CartBendId, changes: Partial<Omit<CartBend, 'cartBendId'>>) => void;
  /** Replaces a bend with a snapshot taken earlier, such as before an edit that was cancelled. */
  restoreBend: (bend: CartBend) => void;
  removeBend: (cartBendId: CartBendId) => void;
  /** Removes the given bends, for example once they have been added to a work order. */
  removeBends: (cartBendIds: readonly CartBendId[]) => void;
  /** Shows a bend on the model. Ignored while a different row is expanded. */
  previewBend: (cartBendId: CartBendId) => void;
  /** Shows the bend being built on the model again. */
  clearPreview: () => void;
  /** Opens a row in place and previews it, closing any other open row. */
  expandBend: (cartBendId: CartBendId) => void;
  /** Closes the open row. Its bend stays on the model, but its row is no longer marked. */
  collapseBend: () => void;
};

const BendCartContext = createContext<BendCartContextType | undefined>(undefined);

/** Holds the bend list: bends built in the Bend Creator that have not been added to a work order yet. */
export const BendCartProvider = ({ children }: { children: ReactNode }) => {
  const [bends, setBends] = useState<readonly CartBend[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [previewedBendId, setPreviewedBendId] = useState<CartBendId | null>(null);
  const [previewMarked, setPreviewMarked] = useState(false);
  const [expandedBendId, setExpandedBendId] = useState<CartBendId | null>(null);
  const [justAddedBendId, setJustAddedBendId] = useState<CartBendId | null>(null);

  const addBend = useCallback<BendCartContextType['addBend']>(
    (bend) => {
      const cartBendId = uuid() as CartBendId;
      if (bends.length === 0) setCartOpen(true);
      setBends((current) => [...current, { ...bend, cartBendId }]);
      setJustAddedBendId(cartBendId);
      return cartBendId;
    },
    [bends.length],
  );

  const duplicateBend = useCallback<BendCartContextType['duplicateBend']>(
    (sourceId) => {
      const source = bends.find(({ cartBendId }) => cartBendId === sourceId);
      if (isNil(source)) return null;
      const cartBendId = uuid() as CartBendId;
      const partName = getFreeName(
        getBaseName(source.values.partName),
        bends.map(({ values }) => values.partName),
      );
      setBends((current) => [
        ...current,
        { ...source, cartBendId, values: { ...source.values, partName }, isAutoNamed: true, duplicatedFrom: sourceId },
      ]);
      setJustAddedBendId(cartBendId);
      setExpandedBendId(cartBendId);
      setPreviewedBendId(cartBendId);
      setPreviewMarked(true);
      return cartBendId;
    },
    [bends],
  );

  const updateBend = useCallback<BendCartContextType['updateBend']>((cartBendId, changes) => {
    setBends((current) => current.map((bend) => (bend.cartBendId === cartBendId ? { ...bend, ...changes } : bend)));
  }, []);

  const restoreBend = useCallback((snapshot: CartBend) => {
    setBends((current) => current.map((bend) => (bend.cartBendId === snapshot.cartBendId ? snapshot : bend)));
  }, []);

  const removeBends = useCallback((cartBendIds: readonly CartBendId[]) => {
    const toRemove = new Set(cartBendIds);
    setBends((current) => current.filter((bend) => !toRemove.has(bend.cartBendId)));
    setPreviewedBendId((current) => (isNil(current) || toRemove.has(current) ? null : current));
    setExpandedBendId((current) => (isNil(current) || toRemove.has(current) ? null : current));
    setJustAddedBendId((current) => (isNil(current) || toRemove.has(current) ? null : current));
  }, []);

  const removeBend = useCallback((cartBendId: CartBendId) => removeBends([cartBendId]), [removeBends]);

  // The just-added highlight is over once anything else is selected, so it can't replay when its row is deselected.
  const previewBend = useCallback(
    (cartBendId: CartBendId) => {
      if (isNil(expandedBendId) || expandedBendId === cartBendId) {
        setPreviewedBendId(cartBendId);
        setPreviewMarked(true);
        setJustAddedBendId((current) => (current === cartBendId ? current : null));
      }
    },
    [expandedBendId],
  );

  const clearPreview = useCallback(() => {
    setPreviewedBendId(null);
    setPreviewMarked(false);
    setJustAddedBendId(null);
  }, []);

  const expandBend = useCallback((cartBendId: CartBendId) => {
    setExpandedBendId(cartBendId);
    setPreviewedBendId(cartBendId);
    setPreviewMarked(true);
    setJustAddedBendId((current) => (current === cartBendId ? current : null));
  }, []);

  const collapseBend = useCallback(() => {
    setExpandedBendId(null);
    setPreviewMarked(false);
    setJustAddedBendId(null);
  }, []);

  const value = useMemo(
    () => ({
      bends,
      cartOpen,
      setCartOpen,
      previewedBendId,
      previewMarked,
      expandedBendId,
      justAddedBendId,
      addBend,
      duplicateBend,
      updateBend,
      restoreBend,
      removeBend,
      removeBends,
      previewBend,
      clearPreview,
      expandBend,
      collapseBend,
    }),
    [
      bends,
      cartOpen,
      previewedBendId,
      previewMarked,
      expandedBendId,
      justAddedBendId,
      addBend,
      duplicateBend,
      updateBend,
      restoreBend,
      removeBend,
      removeBends,
      previewBend,
      clearPreview,
      expandBend,
      collapseBend,
    ],
  );

  return <BendCartContext value={value}>{children}</BendCartContext>;
};

export const useBendCart = () => useGeneralContext(BendCartContext, 'BendCartContext');
